// 添加关注页（自然语言查找）的纯逻辑：字段格式化、列、错误文案、条件回显 → /run 请求体、页面状态机。
// 不碰 React、不碰网络，frontend/tests/screenerModel.test.js 直接覆盖。
// 后端契约见 docs/sdd/astock-agent-v03-requirements-first/06-api-contracts.md「自然语言查找」。

export const PAGE_SIZE = 20;
export const MAX_QUERY_LENGTH = 200;

// 「试试」气泡：点了直接出结果。第 5 条刻意不写「26 年」——跨年就失效了。
// 按名称 / 代码找：排在条件示例前面。
export const LOOKUP_EXAMPLES = ["贵州茅台", "600519", "茅台和五粮液"];

export const EXAMPLES = [
  "市盈率低于 15 的银行股",
  "近一个月跌超 20% 的 ETF",
  "挂钩上证 50 的 ETF",
  "高于 10 倍 PE 的半导体股票",
  "今年以来涨幅超过 50% 的开放基金",
];

export const TYPE_LABELS = { stock: "A股", etf: "ETF", fund: "场外基金" };

// 值的展示方式。数字都是后端给的 6 位小数字符串，页面只负责格式化，不重算。
const FORMATS = {
  price: { kind: "plain", digits: 2 },  // 兜底；表格里的价格走 formatPrice（按类型）
  pct_chg: { kind: "percent", signed: true },
  amount_yi: { kind: "plain", digits: 2, suffix: " 亿" },
  circ_mv_yi: { kind: "plain", digits: 2, suffix: " 亿" },
  total_mv_yi: { kind: "plain", digits: 2, suffix: " 亿" },
  pe_ttm: { kind: "plain", digits: 2 },
  pb: { kind: "plain", digits: 2 },
  ps_ttm: { kind: "plain", digits: 2 },
  dv_ttm: { kind: "percent" },
  revenue_yoy: { kind: "percent", signed: true },
  profit_yoy: { kind: "percent", signed: true },
  profit_qoq: { kind: "percent", signed: true },
  roe: { kind: "percent" },
  pct_chg_5d: { kind: "percent", signed: true },
  turnover_rate: { kind: "percent" },
  listing_age_days: { kind: "plain", digits: 0, suffix: " 天" },
  found_age_days: { kind: "plain", digits: 0, suffix: " 天" },
  limit_up_streak: { kind: "plain", digits: 0, suffix: " 天" },
  up_streak: { kind: "plain", digits: 0, suffix: " 天" },
  down_streak: { kind: "plain", digits: 0, suffix: " 天" },
  limit_down_streak: { kind: "plain", digits: 0, suffix: " 天" },
};

// 动态字段 = 指标 × 窗口（后端 metrics.py / windows.py）：规范写法 `limit_up_days@m202609`，
// 旧写法（ret_10d、limit_up_yday、pct_chg_on_20260930、new_high_250d……）是别名，解析成同一个 (指标, 窗口)。
const LEGACY_STATIC = {
  ret_1m: ["ret", "last21"], ret_3m: ["ret", "last63"], ret_1y: ["ret", "last252"], ret_ytd: ["ret", "ytd"],
  ret_wtd: ["ret", "wtd"], ret_mtd: ["ret", "mtd"], pct_chg_yday: ["pct", "d1"],
  limit_up_yday: ["limit_up_days", "d1"], limit_down_yday: ["limit_down_days", "d1"], limit_up_1m: ["limit_up_days", "last21"],
};
const POINT_METRIC = { pct_chg: "pct", limit_up: "limit_up_days", limit_down: "limit_down_days" };
const LEGACY = [
  [/^ret_(\d+)d$/, (m) => ["ret", `last${m[1]}`]],
  [/^ret_([mw]\d+)$/, (m) => ["ret", m[1]]],
  [/^limit_(up|down)_(\d+)d$/, (m) => [`limit_${m[1]}_days`, `last${m[2]}`]],
  [/^(pct_chg|limit_up|limit_down)_d(\d+)$/, (m) => [POINT_METRIC[m[1]], `d${m[2]}`]],
  [/^(pct_chg|limit_up|limit_down)_on_(\d{8})$/, (m) => [POINT_METRIC[m[1]], `on${m[2]}`]],
  [/^new_(high|low)_(\d+)d$/, (m) => [`new_${m[1]}`, `last${m[2]}`]],
];
const METRICS = new Set(["ret", "pct", "limit_up_days", "limit_down_days", "up_days", "down_days", "new_high", "new_low", "amount_sum", "dividend_yield"]);

export function parseField(field) {
  let pair = null;
  if (field.includes("@")) pair = field.split("@", 2);
  else if (LEGACY_STATIC[field]) pair = LEGACY_STATIC[field];
  else for (const [pattern, build] of LEGACY) { const match = pattern.exec(field); if (match) { pair = build(match); break; } }
  return pair && METRICS.has(pair[0]) && pair[1] ? { metric: pair[0], window: pair[1] } : null;
}

// 单日窗口（昨天 / 某天 / 当日）的涨停 / 跌停是 0 / 1 的「是否」；其余涨停天数、上涨天数是天数。
function dynamicFormat(field) {
  const parsed = parseField(field);
  if (!parsed) return null;
  const { metric, window } = parsed;
  if (metric === "ret" || metric === "pct") return { kind: "percent", signed: true };
  if (metric === "dividend_yield") return { kind: "percent" };  // 股息率不是涨跌，不带正负号、不上红绿色
  if (metric === "amount_sum") return { kind: "plain", digits: 2, suffix: " 亿", sortable: true };
  const single = /^(d\d+|on\d+|last1)$/.test(window);
  if (metric.startsWith("new_") || (metric.startsWith("limit_") && single)) return { kind: "flag", sortable: false };
  return { kind: "plain", digits: 0, suffix: " 天", sortable: true };
}

function formatOf(field) {
  return FORMATS[field] ?? dynamicFormat(field);
}

// 红涨绿跌只给涨跌幅类的列：PE、市值之类没有涨跌含义。
const TONED = new Set(["pct_chg", "pct_chg_5d", "revenue_yoy", "profit_yoy", "profit_qoq"]);
// 后端允许排序的数字列（fields.py 的 sortable）；文本列（行业、类型）不能排。
const SORTABLE = new Set([
  "price", "pct_chg", "amount_yi", "circ_mv_yi", "total_mv_yi", "pe_ttm", "pb", "ps_ttm", "dv_ttm",
  "revenue_yoy", "profit_yoy", "profit_qoq", "roe", "pct_chg_5d", "turnover_rate",
  "listing_age_days", "found_age_days",
]);

export function isMissing(value) {
  return value === null || value === undefined || value === "";
}

function toNumber(value) {
  if (isMissing(value)) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

// 价格的小数位按标的类型：股票 2 位，ETF 3 位，场外基金净值 4 位（和各自的报价习惯一致）。
const PRICE_DIGITS = { stock: 2, etf: 3, fund: 4 };

export function formatPrice(value, type) {
  const parsed = toNumber(value);
  if (parsed === null) return "--";
  const digits = PRICE_DIGITS[type] ?? 2;
  return parsed.toLocaleString("zh-CN", { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

export function formatCell(field, value) {
  if (isMissing(value)) return "--";
  const format = formatOf(field);
  if (!format) return String(value);  // 行业、投资类型这类文本
  const parsed = toNumber(value);
  if (parsed === null) return "--";
  if (format.kind === "flag") return parsed >= 1 ? "是" : "否";
  if (format.kind === "percent") {
    return `${format.signed && parsed > 0 ? "+" : ""}${parsed.toFixed(2)}%`;
  }
  const text = parsed.toLocaleString("zh-CN", {
    minimumFractionDigits: format.digits,
    maximumFractionDigits: format.digits,
  });
  return `${text}${format.suffix ?? ""}`;
}

// "up" / "down" / ""：给涨跌幅类的列上色，0 与缺值不上色。
export function toneOf(field, value) {
  const dynamic = dynamicFormat(field);
  if (!TONED.has(field) && !(dynamic?.kind === "percent" && dynamic.signed)) return "";
  const parsed = toNumber(value);
  if (parsed === null || parsed === 0) return "";
  return parsed > 0 ? "up" : "down";
}

export function isSortable(field) {
  if (SORTABLE.has(field) || /_streak$/.test(field)) return true;
  const dynamic = dynamicFormat(field);
  return dynamic?.kind === "percent" || dynamic?.sortable === true;  // 「是否涨停 / 新高」是 0 / 1，排不排没意义
}

// 价格 | 涨跌 是一格两个数，各自能排序（同概览持仓表）；其余列一列一个字段。
export function nextSort(current, field) {
  const same = current?.field === field;
  return { field, order: same && current.order === "desc" ? "asc" : "desc" };
}

// 名称列字号：名字越长字越小，最小 NAME_MIN_SIZE，再长就省略号（页面上悬停看全名）。
const NAME_FULL_SIZE = 14, NAME_MIN_SIZE = 12, NAME_FIT = 16;  // 14px 能放下约 16 个字
export function nameFontSize(name) {
  const length = [...String(name ?? "")].length;
  if (length <= NAME_FIT) return NAME_FULL_SIZE;
  return Math.max(NAME_MIN_SIZE, Math.round((NAME_FULL_SIZE * NAME_FIT) / length * 2) / 2);
}

export function typeLabel(type) {
  return TYPE_LABELS[type] ?? type;
}

// 一行是否全是场外基金：基金没有当日涨跌，表头与单元格的「涨跌」显示成 --。
// 表格里要显示哪些「额外列」：条件列一定显示；标准列（成交额 / PE / 投资类型…）这一页一个值都没有就不占位
// （混合类别时基金没有 PE、A股没有投资类型，整列 -- 只会让表更宽）。
export function visibleColumns(columns, items) {
  return columns
    .filter((column) => column.field !== "price" && column.field !== "pct_chg")
    .filter((column) => column.condition || items.some((item) => !isMissing(item[column.field])));
}

export function onlyFunds(items) {
  return items.length > 0 && items.every((item) => item.type === "fund");
}

// 把 /nl 的 understood 变成 /run 的请求体。`/run` 不调 LLM、不计费，翻页 / 换排序 / 去条件都走它。
export function buildRunBody(understood, { sort, page = 1, pageSize = PAGE_SIZE } = {}) {
  const body = {
    asset_classes: understood.asset_classes ?? [],
    conditions: (understood.conditions ?? []).map(({ field, op, value }) => ({ field, op, value })),
    page,
    page_size: pageSize,
  };
  const chosen = sort ?? understood.sort;
  if (chosen) body.sort = { field: chosen.field, order: chosen.order };
  if (understood.limit) body.limit = understood.limit;
  return body;
}

// 去掉第 index 个条件后的 understood；条件都去光、也没有排序时返回 null（回到首页态，不能把整个市场列出来）。
export function withoutCondition(understood, index) {
  const conditions = (understood.conditions ?? []).filter((_, i) => i !== index);
  if (!conditions.length && !understood.sort && !understood.limit) return null;
  return { ...understood, conditions };
}

// 错误文案：说清是什么问题、下一步能干什么，不把 HTTP 状态码甩给用户。
export function describeError(error) {
  const status = error?.status;
  if (status === 429) return "查找太频繁，请稍后再试。";
  if (status === 502) return "没能理解这句话，请换个说法，比如写明具体数字。";
  if (status === 400) return error.message || "请输入 1～200 字的查找内容。";
  if (status === 402) return error.message || "光点不足，每次查找需要 10 光点。";
  if (status === 503 && error.payload?.error?.code === "billing_unavailable") return error.message;  // 扣费服务的 503，不是行情的
  if (status === 503) return "市场数据暂时不可用，请稍后重试。";
  if (status === 0) return error.message || "无法连接服务，请检查网络后重试。";
  return error?.message || "暂时无法查找，请稍后重试。";
}

// 页面状态：idle（首页）→ loading → result | error。查询文本 query 是用户输入的原话。
// `seq` 由页面单调递增后随动作传进来；晚到的旧响应对不上当前 seq 就丢掉，避免旧结果盖掉新结果。
export const initialState = { phase: "idle", seq: 0, query: "", queryId: null, understood: null, unsupported: [], result: null, dataDate: {}, dataNote: {}, usage: null, error: "", sort: null, page: 1, busy: false };

export function reducer(state, action) {
  switch (action.type) {
    case "start":   // 提交一句话（/nl）：旧结果先留着不闪，等新结果
      return { ...state, phase: "loading", seq: action.seq, query: action.query, error: "", sort: null, page: 1 };
    case "refine":  // /run（翻页 / 排序 / 去条件）：保持当前结果可见，标记 busy
      return { ...state, seq: action.seq, busy: true, error: "", understood: action.understood ?? state.understood, sort: action.sort ?? state.sort, page: action.page ?? state.page };
    case "restore": // 从别的页面回来：用上次的条件重新走一遍 /run（不调 LLM、不计费）
      return { ...initialState, phase: "result", seq: action.seq, busy: true, query: action.saved.query, understood: action.saved.understood, unsupported: action.saved.unsupported ?? [], sort: action.saved.sort ?? null, page: action.saved.page ?? 1 };
    case "nl_ok":
      if (action.seq !== state.seq) return state;
      return { ...state, phase: "result", busy: false, understood: action.payload.understood, unsupported: action.payload.unsupported ?? [], result: action.payload.result, dataDate: action.payload.data_date ?? {}, dataNote: action.payload.data_note ?? {}, usage: action.payload.usage ?? null, queryId: action.payload.query_id ?? null };
    case "run_ok":
      if (action.seq !== state.seq) return state;
      return { ...state, phase: "result", busy: false, result: action.payload.result, dataDate: action.payload.data_date ?? {}, dataNote: action.payload.data_note ?? {} };
    case "fail":
      if (action.seq !== state.seq) return state;
      // /nl 失败：旧结果属于上一句话，留着会误导 → 清掉进入 error 态（能重试）；
      // /run 失败：旧结果仍和它的条件标签一致，留着并给出内联错误。
      if (action.kind === "nl") {
        return { ...state, phase: "error", busy: false, result: null, understood: null, unsupported: [], error: action.message };
      }
      return { ...state, phase: state.result ? "result" : "error", busy: false, error: action.message };
    case "reset":
      return { ...initialState, seq: action.seq };
    default:
      return state;
  }
}

// 只用名称 / 代码找（没有别的条件）时，返回用户写的名称 / 代码；否则 null。无结果时据此换提示。
export function lookupText(understood) {
  const conditions = understood?.conditions ?? [];
  if (!conditions.length || !conditions.every((condition) => condition.field === "name" || condition.field === "code")) return null;
  const values = conditions.flatMap((condition) => [condition.value].flat()).map((value) => String(value).trim()).filter(Boolean);
  return values.length ? values.join("、") : null;
}

export function totalPages(result) {
  if (!result) return 1;
  return Math.max(1, Math.ceil(result.total / result.page_size));
}

// 「数据日期」一行：按类别各给日期，类别名用中文；日期相同的合并写。
// dataNote 是后端给的口径提示（如 A 股盘中的「实时 11:17」）；没有就是收盘数据，不加任何字样。
export function dataDateText(dataDate, dataNote = {}) {
  const entries = Object.entries(dataDate ?? {}).filter(([, date]) => date);
  if (!entries.length) return "";
  const dates = new Set(entries.map(([, date]) => date));
  if (dates.size === 1) {
    const notes = [...new Set(entries.map(([type]) => dataNote?.[type]).filter(Boolean))];
    return [`数据日期 ${entries[0][1]}`, ...notes].join(" · ");
  }
  return entries
    .map(([type, date]) => [typeLabel(type), date, dataNote?.[type]].filter(Boolean).join(" "))
    .join(" · ");
}

// 从资产页返回时恢复上次的查找：只存条件（不存结果、不存原话以外的东西），回来用 /run 重新取。
const STORAGE_KEY = "screener_last_query";

export function saveLast(state) {
  if (!state.understood || state.phase !== "result") return;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
      query: state.query, understood: state.understood, unsupported: state.unsupported, sort: state.sort, page: state.page,
    }));
  } catch { /* 隐私模式 / 存储被禁：恢复只是便利，丢了不影响查找 */ }
}

export function loadLast() {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    const saved = raw ? JSON.parse(raw) : null;
    return saved?.understood && typeof saved.query === "string" ? saved : null;
  } catch {
    return null;
  }
}

export function clearLast() {
  try { window.sessionStorage.removeItem(STORAGE_KEY); } catch { /* 同上 */ }
}
